import React, { useEffect } from 'react';
import { Form, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getLostAndFoundItems } from '@/api/lostAndFound';
import { useAuth } from '@/contexts/AuthContext';
import type { LostAndFoundItem } from '@/types/community/lostAndFound';

interface LostAndFoundSelectorProps {
  disabled?: boolean;
  fieldName?: string;
  onLostAndFoundDataChange?: (item: { id: string; name: string } | null) => void;
}

export const LostAndFoundSelector: React.FC<LostAndFoundSelectorProps> = ({
  disabled = false,
  fieldName = 'lostAndFoundId',
  onLostAndFoundDataChange
}) => {
  const { t } = useTranslation(['lostAndFound', 'common']);
  const { firestoreUser } = useAuth();
  const form = Form.useFormInstance();

  // Fetch lost and found items
  const { data: itemsData, error } = useQuery({
    queryKey: ['lostAndFound', firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getLostAndFoundItems({ 
        firestoreUser,
        pageSize: 100 // Get all items for selector
      });
    },
    enabled: !!firestoreUser?.workspace?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Initialize item data from form values when component mounts or form values change
  useEffect(() => {
    const itemId = form.getFieldValue(fieldName);
    if (itemId && itemsData?.items) {
      const item = itemsData.items.find((i: LostAndFoundItem) => i.id === itemId);
      onLostAndFoundDataChange?.(item ? { 
        id: item.id, 
        name: item.title 
      } : null);
    }
  }, [form, itemsData, onLostAndFoundDataChange, fieldName]);

  const handleItemChange = (itemId: string) => {
    // Provide item data to parent
    if (itemId) {
      const item = itemsData?.items?.find((i: LostAndFoundItem) => i.id === itemId);
      onLostAndFoundDataChange?.(item ? { 
        id: item.id, 
        name: item.title 
      } : null);
    } else {
      onLostAndFoundDataChange?.(null);
    }
  };

  const itemOptions = itemsData?.items?.map((item: LostAndFoundItem) => ({
    value: item.id,
    label: item.title,
  })) || [];

  // Show error state if API fails
  if (error) {
    return (
      <Form.Item
        name={fieldName}
        label={t('lostAndFound:fields.title')}
      >
        <Select
          placeholder={t('lostAndFound:fields.selectItem')}
          disabled={true}
          options={[]}
        />
      </Form.Item>
    );
  }

  return (
    <Form.Item
      name={fieldName}
      label={t('lostAndFound:fields.title')}
    >
      <Select
        placeholder={t('lostAndFound:fields.selectItem')}
        onChange={handleItemChange}
        disabled={disabled}
        allowClear
        options={itemOptions}
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
        }
      />
    </Form.Item>
  );
}; 